import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Company } from 'src/app/model/contacts/company';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { TabService } from 'src/app/core/services/tab.service';
import { ConfirmationService, FilterMetadata } from '@openng/optimus-ui/api';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { COMPANY_TABLE_DEFINITION } from '../../definitions/data-table-definitions';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CompanyDataTableInput } from 'src/app/model/admin/company-data-table-input';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { CheckboxChangeEvent, Checkbox } from '@openng/optimus-ui/checkbox';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { CompanyTableService } from 'src/app/services/table/company-table.service';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { AdminApiService } from 'src/app/services/api/admin-api.service';

@Component({
  selector: 'app-company-container',
  templateUrl: './company-container.component.html',
  styleUrls: ['./company-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Checkbox,
    TranslatePipe,
  ],
})
export class CompanyContainerComponent
  extends SearchContainerComponent<Company>
  implements OnInit, OnDestroy
{
  protected _contactApi = inject(ContactApiService);
  protected _adminBuilder = inject(AdminBuilderService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _companyTableService = inject(CompanyTableService);
  protected _fb = inject(UntypedFormBuilder);
  protected _confirmationService = inject(ConfirmationService);
  protected _authApi = inject(AuthApiService);
  protected _adminApi = inject(AdminApiService);

  canEdit = false;
  canDelete = false;
  canCreate = false;
  saving = false;
  form!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader: string = '';
  filteredContacts: any[] = [];

  companies: Company[] = [];
  selectedCompany?: Company;
  showSuccessMessage = true;

  activeTabChangedSub!: Subscription;
  hasLoaded = false;

  constructor() {
    super();
  }

  ngOnInit() {
    this._companyTableService.setLastLazyLoad(null);
    this.setTableService(this._companyTableService);
    this.setupActiveTabChangedSubscription();
    this.form = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: true }, Validators.required],
      abbreviation: [{ value: null, disabled: true }, Validators.required],
      enabled: [{ value: false, disabled: true }],
      version: [null],
    });

    this.columns = COMPANY_TABLE_DEFINITION;

    const screenName$ = this._translateService.get('CONTACT.SCREEN.COMPANIES');
    const dialogHeader$ = this._translateService.get(
      'CONTACT.LABEL.COMPANY_DETAILS'
    );
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.COMPANIES.CREATE_COMPANIES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.COMPANIES.EDIT_COMPANIES,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.COMPANIES.DELETE_COMPANIES,
    ]);

    this.form.disable();
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._companyTableService.setLastLazyLoad($event.lazy);
    const query: any = {
      name: null,
      enabled: null,
      abbreviation: null,
    };

    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }
    if (filters.abbreviation) {
      query.abbreviation = (filters.abbreviation as FilterMetadata).value;
    }
    if (
      filters.enabled &&
      !isNullOrUndefined((filters.enabled as FilterMetadata).value)
    ) {
      if ((filters.enabled as FilterMetadata).value) {
        query.enabled = 'true';
      } else {
        query.enabled = 'false';
      }
    }

    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');
    this._adminApi
      .queryForCompanies(
        req.pageSize,
        req.page,
        query as CompanyDataTableInput,
        sort
      )
      .subscribe({
        next: (companyConnection) => {
          this.companies = companyConnection.items;
          this.totalRecords = companyConnection.totalRecords;
          this.elements = [...this.companies];
          this.hasLoaded = true;
        },
        error: (error) => {
          this.loading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CONTACT.MESSAGES.ERROR.QUERY_COMPANIES',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.loading = false;
          if (this.showSuccessMessage) {
            this.showSuccessMessage = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'CONTACT.MESSAGES.SUCCESS.QUERY_COMPANIES',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          }
        },
      });
  }

  changeViewEvent($event: { data: Company }): void {
    this.selectedCompany = $event.data;
    this.form.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      abbreviation: $event.data.abbreviation,
      enabled: $event.data.enabled,
      version: $event.data.version,
    });
    this.displayDialog = true;

    if (this.canEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  clearForm(): void {
    this.form.reset();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedCompany = undefined;
  }

  newCompany(): void {
    this.form.reset();
    this.form.controls['enabled'].patchValue(false, { emitEvent: true });
    this.displayDialog = true;

    if (this.canCreate) {
      this.form.enable();
    }
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._companyTableService.getLastLazyLoad()! });
  }

  nameChanged(): void {
    if (this.selectedCompany) {
      this.selectedCompany.name = this.form.controls['name'].value;
    }
  }

  abbrChanged(): void {
    if (this.selectedCompany) {
      this.selectedCompany.abbreviation =
        this.form.controls['abbreviation'].value;
    }
  }

  toggleRecord($event: CheckboxChangeEvent): void {
    if (!isNullOrUndefined($event) && this.selectedCompany) {
      this.selectedCompany.enabled = $event.checked;
    }
  }

  saveCompany(): void {
    if (!this.canEdit && !this.canCreate) {
      return;
    }

    if (!isNullOrUndefined(this.form) && this.form.valid) {
      let company = null;
      if (!isNullOrUndefined(this.selectedCompany)) {
        company = this.selectedCompany;
      } else {
        company = Object.assign({}, this.form.getRawValue());
      }
      if (!company.id) {
        this.createCompany(company);
      } else {
        this.updateCompany(company, true);
      }
    }
  }

  createCompany(company: Company) {
    if (!this.canCreate) {
      return;
    }

    company.dirtyStatus = DirtyStatus.NEW;
    const input: CompanyDataTableInput | null =
      this._adminBuilder.buildCompanyDataTableInput(company, true);

    this.saving = true;
    this._adminApi.createCompany(input).subscribe({
      next: (result) => {
        this.clearForm();
        this.displayDialog = false;
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CONTACT.MESSAGES.ERROR.CREATE_COMPANY',
          bodyParams: { name: company.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'CONTACT.MESSAGES.SUCCESS.CREATE_COMPANY',
          bodyParams: { name: company.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
        this.refresh();
      },
    });
  }

  updateCompany(changes: Company, closeDialog: boolean) {
    if (!this.canEdit) {
      return;
    }

    changes.dirtyStatus = DirtyStatus.UPDATED;
    const companyChanges: CompanyDataTableInput | null =
      this._adminBuilder.buildCompanyDataTableInput(changes, false);

    this.saving = true;
    this._adminApi.updateCompany(changes.id, companyChanges).subscribe({
      next: (result) => {
        if (!closeDialog && !isNullOrUndefined(result)) {
          this.changeViewEvent({ data: result });
        } else {
          this.clearForm();
          this.displayDialog = false;
          this.refresh();
        }
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CONTACT.MESSAGES.ERROR.UPDATE_COMPANY',
          bodyParams: { name: changes.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'CONTACT.MESSAGES.SUCCESS.UPDATE_COMPANY',
          bodyParams: { name: changes.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
      },
    });
  }

  deleteCompany(): void {
    if (!this.canDelete) {
      return;
    }
    if (!isNullOrUndefined(this.form)) {
      const company = Object.assign({}, this.form.getRawValue());
      let confHeader = null,
        ok = null,
        cancel = null,
        message = null;
      const confHeader$ = this._translateService.get(
        'CONTACT.MESSAGES.HEADERS.DELETE_COMPANY'
      );
      const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
      const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
      const message$ = this._translateService.get(
        'CONTACT.MESSAGES.CONFIRMATION.DELETE_COMPANY',
        { name: company.name }
      );

      forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
        confHeader = messages[0];
        ok = messages[1];
        cancel = messages[2];
        message = messages[3];
      });

      this._confirmationService.confirm({
        header: confHeader!,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok!,
        rejectLabel: cancel!,
        message: message!,
        accept: () => {
          this.saving = true;
          if (!isNullOrUndefined(company.id)) {
            // Existing call type so delete it
            this._adminApi
              .allowCompanyDelete(company.id)
              .subscribe((canDelete) => {
                if (!isNullOrUndefined(canDelete) && !canDelete) {
                  this.saving = false;
                  this._logAndMessage.translateToErrorAlert({
                    bodyKey:
                      'Delete cannot be performed, the entry is already associated with records.', //canDelete.data.allowCompanyDelete.message,
                    headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                  });
                } else {
                  this._adminApi.deleteCompany(company.id).subscribe({
                    next: (result) => {
                      this.saving = false;
                      this.clearForm();
                      this.displayDialog = false;
                    },
                    error: (error) => {
                      this._logAndMessage.errorLogOnly(error);
                      this._logAndMessage.translateToErrorAlert({
                        bodyKey: 'CONTACT.MESSAGES.ERROR.DELETE_COMPANY',
                        bodyParams: { name: company.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                      });
                    },
                    complete: () => {
                      this._logAndMessage.translateToSuccessMessage({
                        bodyKey: 'CONTACT.MESSAGES.SUCCESS.DELETE_COMPANY',
                        bodyParams: { name: company.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                      });
                      this.refresh();
                    },
                  });
                }
              });
          } else {
            // new call type so just close dialog
            this.displayDialog = false;
          }
        },
        reject: () => {},
      });
    }
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
